import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useAuth } from "./context/AuthContext";
import {
  StudentAuthProvider,
  useStudentAuth,
} from "./context/StudentAuthContext";
// import { MasterAuthProvider } from "./context/MasterAuthContext"; // <-- Import MasterAuthProvider
import { MasterAuthProvider, useMasterAuth } from "./context/MasterAuthContext";

// Layout
import Layout from "./components/layout/Layout";

// Public Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import SchoolRegister from "./pages/SchoolRegister";
import StudentLogin from "./pages/student/StudentLogin";
import RegistrationSuccess from "./pages/RegistrationSuccess";
// import profileSettings from "./pages/ProfileSettings";
// Master Admin
import MasterLogin from "./pages/master/Login";
import MasterDashboard from "./pages/master/Dashboard";
import PendingApprovals from "./pages/master/PendingApprovals";
import SchoolsList from "./pages/master/SchoolsList";
import PaymentsList from "./pages/master/PaymentsList";
import TransactionsList from "./pages/master/TransactionsList";
import ManualRequests from "./pages/master/ManualRequests";

// Admin
import AdminDashboard from "./pages/admin/Dashboard";
import StaffManagement from "./pages/admin/StaffManagement";
import StudentManagement from "./pages/admin/StudentManagement";
import ClassManagement from "./pages/admin/ClassManagement";
import SubjectManagement from "./pages/admin/SubjectManagement";
import AssessmentConfig from "./pages/admin/AssessmentConfig";
import AcademicSessionManagement from "./pages/admin/AcademicSessionManagement";
import StudentEnrollment from "./pages/admin/StudentEnrollment";
import AdminReportDashboard from "./pages/admin/AdminReportDashboard";
import CommentFieldsConfig from "./pages/admin/CommentFieldsConfig";
import ReportConfig from "./pages/admin/ReportConfig";
import PayFeePage from "./pages/admin/PayFeePage";
import WalletPage from "./pages/admin/WalletPage";
import PaymentCallback from "./pages/admin/PaymentCallback";
// import adminMasterResultSheet from "./pages/admin/AdminMasterResultSheet";
import ProfileSettings from "./pages/ProfileSettings";

// Teacher
import TeacherDashboard from "./pages/teacher/Dashboard";
import CaptureAssessment from "./pages/teacher/CaptureAssessment";
import StudentProgress from "./pages/teacher/StudentProgress";
import TeacherGrading from "./pages/teacher/TeacherGrading";
import SubjectRankings from "./pages/teacher/SubjectRanking";

// Form Master
import FormMasterDashboard from "./pages/form-master/Dashboard";
import ClassStudents from "./pages/form-master/ClassStudents";
import CalculateGrades from "./pages/form-master/CalculateGrades";
import ClassReports from "./pages/form-master/ClassReports";
import MasterResultSheet from "./pages/form-master/MasterResultSheet";

// Student
import StudentDashboard from "./pages/student/Dashboard";
import MyGrades from "./pages/student/MyGrades";
import MyProfile from "./pages/student/MyProfile";
import StudentResultPage from "./pages/student/StudentResultPage";
import AdminMasterResultSheet from "./pages/admin/AdminMasterResultSheet";

// quiz system
import AdminQuizList from './pages/admin/quizzes/AdminQuizList';
import AdminCreateQuiz from './pages/admin/quizzes/AdminCreateQuiz';
import AdminQuizDetails from './pages/admin/quizzes/AdminQuizDetails';
import AdminImportQuizQuestions from './pages/admin/quizzes/AdminImportQuizQuestions';
import AdminEditQuiz  from './pages/admin/quizzes/AdminEditQuiz';
import AdminAssignQuiz from './pages/admin/quizzes/AdminAssignQuiz';
import AdminQuizAttempts from './pages/admin/quizzes/AdminQuizAttempts';

// staff
import StaffQuizList from './pages/teacher/quizzes/StaffQuizList';
import StaffCreateQuiz from './pages/teacher/quizzes/StaffCreateQuiz';
import StaffQuizDetails from './pages/teacher/quizzes/StaffQuizDetails';
import StaffImportQuizQuestions from './pages/teacher/quizzes/StaffImportQuizQuestions';
import StaffEditQuiz  from './pages/teacher/quizzes/StaffEditQuiz';
import StaffAssignQuiz from './pages/teacher/quizzes/StaffAssignQuiz';
import StaffQuizAttempts from './pages/teacher/quizzes/StaffQuizAttempts';

// student's
import StudentQuizList from './pages/student/quizzes/StudentQuizList';
import StudentQuizIntro from './pages/student/quizzes/StudentQuizIntro';
import StudentQuizTaking from './pages/student/quizzes/StudentQuizTaking';
import StudentQuizAttempts from './pages/student/quizzes/StudentQuizAttempts';
import StudentQuizReview from './pages/student/quizzes/StudentQuizReview';

/* ============================
   PROTECTED ROUTES
============================ */

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );

  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role))
    return <Navigate to="/login" replace />;

  return <Layout>{children}</Layout>;
};


const MasterRoute = ({ children }) => {
  const { isMasterAuthenticated, loading } = useMasterAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!isMasterAuthenticated) {
    return <Navigate to="/master/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const StudentRoute = ({ children }) => {
  const { student, loading, isAuthenticated } = useStudentAuth();

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );

  if (!isAuthenticated || !student)
    return <Navigate to="/student_login" replace />;

  return <Layout>{children}</Layout>;
};

/* ============================
   APP
============================ */

function App() {
  return (
    <>
      <Toaster position="top-right" />
      {/* MasterAuthProvider must be inside Router (already provided by parent) */}
      <MasterAuthProvider>
        <StudentAuthProvider>
          <Routes>
            {/* ================= PUBLIC ================= */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot_password" element={<ForgotPassword />} />
            <Route path="/school/register" element={<SchoolRegister />} />
            <Route path="/student_login" element={<StudentLogin />} />
            <Route path="/master/login" element={<MasterLogin />} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
            {/* ================= MASTER ================= */}
            <Route
              path="/master/dashboard"
              element={
                <MasterRoute allowedRoles={["master_admin"]}>
                  <MasterDashboard />
                </MasterRoute>
              }
            />

            <Route
              path="/master/manual-requests"
              element={
                <MasterRoute allowedRoles={["master_admin"]}>
                  <ManualRequests />
                </MasterRoute>
              }
            />
            <Route
              path="/master/pending"
              element={
                <MasterRoute allowedRoles={["master_admin"]}>
                  <PendingApprovals />
                </MasterRoute>
              }
            />
            <Route
              path="/master/schools"
              element={
                <MasterRoute allowedRoles={["master_admin"]}>
                  <SchoolsList />
                </MasterRoute>
              }
            />
            <Route
              path="/master/payments"
              element={
                <MasterRoute allowedRoles={["master_admin"]}>
                  <PaymentsList />
                </MasterRoute>
              }
            />
            <Route
              path="/master/transactions"
              element={
                <MasterRoute allowedRoles={["master_admin"]}>
                  <TransactionsList />
                </MasterRoute>
              }
            />

            {/* ================= ADMIN ================= */}
            <Route path="/admin/quizzes/:id/edit" element={<AdminEditQuiz />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/master-result-sheet"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <AdminMasterResultSheet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/staff"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <StaffManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/students"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <StudentManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/student-enrollment"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <StudentEnrollment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/classes"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <ClassManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subjects"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <SubjectManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/assessment-config"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <AssessmentConfig />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/academic-sessions"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <AcademicSessionManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/report-data"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <AdminReportDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/comment-config"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <CommentFieldsConfig />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/report-config"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <ReportConfig />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pay-fee"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <PayFeePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/wallet"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <WalletPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payment-callback"
              element={
                <ProtectedRoute allowedRoles={["lead_one"]}>
                  <PaymentCallback />
                </ProtectedRoute>
              }
            />

            {/* pgneral route */}
            <Route
  path="/profile"
  element={
    <ProtectedRoute allowedRoles={['lead_one', 'lead_two', 'lead_four']}>
      <ProfileSettings />
    </ProtectedRoute>
  }
/>


            {/* ================= FORM MASTER ================= */}
            <Route
              path="/form-master/dashboard"
              element={
                <ProtectedRoute allowedRoles={["lead_two"]}>
                  <FormMasterDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/form-master/master-result-sheet" 
            element={
                <ProtectedRoute allowedRoles={["lead_two"]}>
                  <MasterResultSheet />
                </ProtectedRoute>
              }
            
            />
            <Route
              path="/form-master/class-students"
              element={
                <ProtectedRoute allowedRoles={["lead_two"]}>
                  <ClassStudents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/form-master/calculate-grades"
              element={
                <ProtectedRoute allowedRoles={["lead_two"]}>
                  <CalculateGrades />
                </ProtectedRoute>
              }
            />
            <Route
              path="/form-master/reports"
              element={
                <ProtectedRoute allowedRoles={["lead_two"]}>
                  <ClassReports />
                </ProtectedRoute>
              }
            />

            {/* ================= TEACHER ================= */}
            <Route
              path="/teacher/dashboard"
              element={
                <ProtectedRoute allowedRoles={["lead_four"]}>
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/capture-assessment"
              element={
                <ProtectedRoute allowedRoles={["lead_four"]}>
                  <CaptureAssessment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/student-progress"
              element={
                <ProtectedRoute allowedRoles={["lead_four"]}>
                  <StudentProgress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/subject-grade"
              element={
                <ProtectedRoute allowedRoles={["lead_four"]}>
                  <TeacherGrading />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/subject-ranking"
              element={
                <ProtectedRoute allowedRoles={["lead_four"]}>
                  <SubjectRankings />
                </ProtectedRoute>
              }
            />

            {/* ================= STUDENT ================= */}
            <Route
              path="/student/dashboard"
              element={
                <StudentRoute>
                  <StudentDashboard />
                </StudentRoute>
              }
            />
            <Route
              path="/student/grades"
              element={
                <StudentRoute>
                  <MyGrades />
                </StudentRoute>
              }
            />
            <Route
              path="/student/profile"
              element={
                <StudentRoute>
                  <MyProfile />
                </StudentRoute>
              }
            />
            <Route
              path="/student/student-result-page"
              element={
                <StudentRoute>
                  <StudentResultPage />
                </StudentRoute>
              }
            />




            {/* ================= Quiz routes ================= */}
            <Route
  path="/admin/quizzes"
  element={
    <ProtectedRoute allowedRoles={["lead_one", "lead_four"]}>
      <AdminQuizList />
    </ProtectedRoute>
  }
/>
<Route
  path="/admin/quizzes/create"
  element={
    <ProtectedRoute allowedRoles={["lead_one", "lead_four"]}>
      <AdminCreateQuiz />
    </ProtectedRoute>
  }
/>
<Route
  path="/admin/quizzes/:quizId"
  element={
    <ProtectedRoute allowedRoles={["lead_one", "lead_four"]}>
      <AdminQuizDetails />
    </ProtectedRoute>
  }
/>
<Route
  path="/admin/quizzes/:quizId/import-questions"
  element={
    <ProtectedRoute allowedRoles={["lead_one", "lead_four"]}>
      <AdminImportQuizQuestions />
    </ProtectedRoute>
  }
/>
<Route
  path="/admin/quizzes/:quizId/assign"
  element={
    <ProtectedRoute allowedRoles={["lead_one", "lead_four"]}>
      <AdminAssignQuiz />
    </ProtectedRoute>
  }
/>
<Route
  path="/admin/quizzes/:quizId/attempts"
  element={
    <ProtectedRoute allowedRoles={["lead_one", "lead_four"]}>
      <AdminQuizAttempts />
    </ProtectedRoute>
  }
/>

{/* teacher role */}

{/* <Route path="/teacher/quizzes/:id/edit" element={<StaffEditQuiz />} /> */}
            <Route
              path="/teacher/quizzes/:id/edit"
              element={
                <ProtectedRoute allowedRoles={["lead_four"]}>
                  <StaffEditQuiz />
                </ProtectedRoute>
              }
            />

<Route
  path="/teacher/quizzes"
  element={
    <ProtectedRoute allowedRoles={["lead_four"]}>
      <StaffQuizList />
    </ProtectedRoute>
  }
/>
<Route
  path="/teacher/quizzes/create"
  element={
    <ProtectedRoute allowedRoles={["lead_four"]}>
      <StaffCreateQuiz />
    </ProtectedRoute>
  }
/>
<Route
  path="/teacher/quizzes/:quizId"
  element={
    <ProtectedRoute allowedRoles={["lead_four"]}>
      <StaffQuizDetails />
    </ProtectedRoute>
  }
/>
<Route
  path="/teacher/quizzes/:quizId/import-questions"
  element={
    <ProtectedRoute allowedRoles={["lead_four"]}>
      <StaffImportQuizQuestions />
    </ProtectedRoute>
  }
/>
<Route
  path="/teacher/quizzes/:quizId/assign"
  element={
    <ProtectedRoute allowedRoles={["lead_four"]}>
      <StaffAssignQuiz />
    </ProtectedRoute>
  }
/>
<Route
  path="/teacher/quizzes/:quizId/attempts"
  element={
    <ProtectedRoute allowedRoles={["lead_four"]}>
      <StaffQuizAttempts />
    </ProtectedRoute>
  }
/>

{/* Student Quiz Routes */}
<Route
  path="/student/quizzes"
  element={
    <StudentRoute>
      <StudentQuizList />
    </StudentRoute>
  }
/>
<Route
  path="/student/quiz/:quizId"
  element={
    <StudentRoute>
      <StudentQuizIntro />
    </StudentRoute>
  }
/>
<Route
  path="/student/quiz/:quizId/take"
  element={
    <StudentRoute>
      <StudentQuizTaking />
    </StudentRoute>
  }
/>
<Route
  path="/student/quiz/:quizId/attempts"
  element={
    <StudentRoute>
      <StudentQuizAttempts />
    </StudentRoute>
  }
/>
<Route
  path="/student/quiz/:quizId/attempt/:attemptId/review"
  element={
    <StudentRoute>
      <StudentQuizReview />
    </StudentRoute>
  }
/>
            {/* ================= FALLBACK ================= */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </StudentAuthProvider>
      </MasterAuthProvider>
    </>
  );
}

export default App;

